/* eslint-disable max-lines */
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  hoveredPinIdAtom,
  hoveredPinSourceAtom,
  pinsAtom,
  useRemovePin,
  useSubmitPinMessage,
  type AcknowledgmentPayload,
  type Clarification,
  type PinMessage,
  type Pin as PinType,
  type ResultPayload,
  type TextInfo
} from "@design-lab/entities/pinning";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import {
  type AnalyticsClarification,
  trackDesignLabPinClarificationClicked,
  trackDesignLabPinDeleteFailed,
  trackDesignLabPinDeleted,
  trackDesignLabPinError,
  trackDesignLabPinMessageSubmitted,
  trackDesignLabPinNeedsClarification,
  trackDesignLabPinReady,
  trackDesignLabPinSelected,
  trackDesignLabPinUnsupported
} from "@design-lab/shared/lib/analytics";
import { useToast } from "@design-lab/shared/ui";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { TextField } from "@design-lab/shared/ui/design-system/TextField";
import { MarkdownContent, processTextWithHexCodes } from "@design-lab/shared/ui/MarkdownContent";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Loader2, Pin, Sparkles, Trash2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useGoogleFont } from "./useGoogleFont";

const MIN_MESSAGE_LENGTH = 1;
const CLARIFICATION_CONTAINER_MAX_HEIGHT = 280;

function getMessageCounts(
  messages: PinMessage[],
  pendingRole?: "user" | "ai"
): { userMessagesCount: number; aiMessagesCount: number } {
  const userMessagesCount =
    messages.filter(m => m.role === "user").length + (pendingRole === "user" ? 1 : 0);
  const aiMessagesCount =
    messages.filter(m => m.role === "ai").length + (pendingRole === "ai" ? 1 : 0);
  return { userMessagesCount, aiMessagesCount };
}

type FontPreviewOptionProps = {
  textValue: string;
  fontFamily: string;
  color: string;
  optionLabel: string;
  optionValue: string;
  isSelected: boolean;
  onPress: () => void;
  isDisabled: boolean;
  previewType: "font" | "color";
};

function FontPreviewOption({
  textValue,
  fontFamily,
  color,
  optionLabel,
  optionValue,
  isSelected,
  onPress,
  isDisabled,
  previewType
}: FontPreviewOptionProps) {
  // Load the font if this is a font preview
  const fontToLoad = previewType === "font" ? optionValue : fontFamily;
  useGoogleFont(fontToLoad);

  // Determine the display color - for font preview use the passed color, for color preview use the option value
  const displayColor = previewType === "color" ? optionValue : color;
  const displayFont = previewType === "font" ? optionValue : fontFamily;

  // For color options, show both label and hex code
  const secondaryLabel = previewType === "color" ? `${optionLabel} (${optionValue})` : optionLabel;

  return (
    <div
      data-testid={`pin-clarify-preview-${optionLabel}`}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : onPress}
      onKeyDown={e => {
        if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onPress();
        }
      }}
      className={`flex w-[180px] cursor-pointer flex-col items-center justify-center rounded-lg border px-3 py-3 ${
        isSelected
          ? "border-blue-500 bg-blue-50 hover:bg-blue-100"
          : "border-gray-300 bg-white hover:bg-gray-50"
      } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <div
        className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-lg font-bold"
        style={{
          fontFamily: `"${displayFont}", sans-serif`,
          color: displayColor
        }}
      >
        {textValue}
      </div>
      <div className="mt-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-xs text-gray-500">
        {secondaryLabel}
      </div>
    </div>
  );
}

type ClarificationResponse = {
  selected: string | null;
  custom: string;
  isTypingCustom: boolean;
};

/**
 * Gets the effective answer value for a clarification response.
 */
function getClarificationAnswer(response: ClarificationResponse | undefined): string | null {
  if (!response) return null;
  if (response.isTypingCustom) {
    return response.custom?.trim() || null;
  }
  return response.selected || null;
}

type ClarificationSectionProps = {
  clarification: Clarification;
  response: ClarificationResponse;
  onSelectOption: (value: string | null) => void;
  onToggleCustomInput: () => void;
  onCustomInputChange: (value: string) => void;
  onSubmit: () => void;
  isDisabled: boolean;
  textInfo?: TextInfo | undefined;
  selectedFont?: string | null | undefined;
  selectedColor?: string | null | undefined;
};

function ClarificationSection({
  clarification,
  response,
  onSelectOption,
  onToggleCustomInput,
  onCustomInputChange,
  onSubmit,
  isDisabled,
  textInfo,
  selectedFont,
  selectedColor
}: ClarificationSectionProps) {
  const isFontSelection = clarification.type === "font_selection";
  const isFontColor = clarification.type === "font_color";
  const isColorShade = clarification.type === "color_shade";
  const usesFontPreview = isFontSelection || isFontColor;

  // Get text value for preview - fallback to clarification label if text.value is null
  const textValue = textInfo?.value || clarification.label || "Sample";
  // Get base font and color from textInfo
  const baseFont = textInfo?.fontFamily || "inherit";
  const baseColor = textInfo?.color || "#000000";

  // For cross-updating: use selected values if available
  const effectiveFont = selectedFont || baseFont;
  const effectiveColor = selectedColor || baseColor;

  return (
    <div
      className="flex flex-col gap-2"
      data-clarification-label={clarification.label || clarification.question}
    >
      <div className="text-sm text-gray-800">{clarification.question}</div>
      <div className="flex flex-wrap gap-2">
        {clarification.options.map(opt => {
          const canonicalValue = opt.value ?? opt.label;
          const isSelected = !response.isTypingCustom && response.selected === canonicalValue;

          // Use FontPreviewOption for font_selection and font_color types
          if (usesFontPreview) {
            return (
              <FontPreviewOption
                key={`${opt.label}-${opt.value}`}
                textValue={textValue}
                fontFamily={isFontSelection ? opt.value || opt.label : effectiveFont}
                color={isFontColor ? opt.value || opt.label : effectiveColor}
                optionLabel={opt.label}
                optionValue={opt.value || opt.label}
                isSelected={isSelected}
                onPress={() => onSelectOption(isSelected ? null : canonicalValue)}
                isDisabled={isDisabled}
                previewType={isFontSelection ? "font" : "color"}
              />
            );
          }

          const displayText = isColorShade && opt.value ? `${opt.label} (${opt.value})` : opt.label;

          return (
            <div
              key={`${opt.label}-${opt.value}`}
              data-testid={`pin-clarify-${opt.label}`}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onClick={
                isDisabled ? undefined : () => onSelectOption(isSelected ? null : canonicalValue)
              }
              onKeyDown={e => {
                if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onSelectOption(isSelected ? null : canonicalValue);
                }
              }}
              className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium ${
                isSelected
                  ? "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {processTextWithHexCodes(displayText)}
            </div>
          );
        })}
      </div>
      {response.isTypingCustom ? (
        <div className="flex items-center gap-2">
          <TextField
            value={response.custom}
            onChange={onCustomInputChange}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === "Return") {
                e.preventDefault();
                if (!isDisabled) {
                  onSubmit();
                }
              }
            }}
            placeholder="Type your answer..."
            className="flex-1"
            isDisabled={isDisabled}
            autoFocus
            data-testid={`pin-clarify-custom-input-${clarification.type}`}
          />
          <Button
            variant="link"
            size="sm"
            onPress={onToggleCustomInput}
            isDisabled={isDisabled}
            className="text-gray-500 no-underline hover:text-gray-700 hover:no-underline"
            data-testid={`pin-clarify-cancel-custom-${clarification.type}`}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          onClick={isDisabled ? undefined : onToggleCustomInput}
          onKeyDown={e => {
            if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onToggleCustomInput();
            }
          }}
          data-testid={`pin-clarify-type-own-${clarification.type}`}
          className={`cursor-pointer self-start rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 ${
            isDisabled ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          + Type my own
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
export function PinningPanel() {
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentSessionId = currentPinningSession?.sessionId;

  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const setHoveredPinId = useSetAtom(hoveredPinIdAtom);
  const setHoveredPinSource = useSetAtom(hoveredPinSourceAtom);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState<Set<string>>(new Set());
  const [pendingDeletePinId, setPendingDeletePinId] = useState<string | null>(null);
  const [clarificationResponses, setClarificationResponses] = useState<
    Record<string, ClarificationResponse>
  >({});

  const clearRemovingPin = useCallback((pinId: string) => {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }, []);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursorPositionRef = useRef<number | null>(null);
  const clarificationScrollRef = useRef<HTMLDivElement>(null);
  const [unansweredAbove, setUnansweredAbove] = useState(0);
  const [unansweredBelow, setUnansweredBelow] = useState(0);

  const { mutate: removePinApi } = useRemovePin();
  const { error: showErrorToast } = useToast();

  const activePin = useMemo(
    () => pins.find(p => p.id === activePinId) ?? null,
    [activePinId, pins]
  );

  // Clear pins and active pin when pinning mode is closed
  useEffect(
    () => () => {
      setPins([]);
      setActivePinId(null);
    },
    [setPins, setActivePinId]
  );

  useEffect(() => {
    if (activePinId) return;
    if (pins.length === 0) return;
    setActivePinId(pins[0]?.id ?? null);
  }, [activePinId, pins, setActivePinId]);

  useEffect(() => {
    setComposerText("");
    setClarificationResponses({});
  }, [activePinId]);

  // Auto-focus and scroll to composer when it becomes available
  useEffect(() => {
    // Focus when:
    // 1. New pin (draft with no messages)
    // 2. Follow-up question needed (needs_clarification state)
    // 3. Pin returns to draft state after processing
    const shouldFocus =
      (activePin?.state === "draft" && activePin.messages.length === 0) ||
      activePin?.state === "needs_clarification";

    if (shouldFocus && textareaRef.current) {
      // Small timeout to ensure DOM is ready and animation is complete
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }, 100);
    }
  }, [activePin?.state, activePin?.messages.length, activePinId]);

  // Handle cursor positioning after inserting newline with Cmd/Ctrl+Enter
  useLayoutEffect(() => {
    if (pendingCursorPositionRef.current !== null && textareaRef.current) {
      const pos = pendingCursorPositionRef.current;
      textareaRef.current.selectionStart = pos;
      textareaRef.current.selectionEnd = pos;
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      pendingCursorPositionRef.current = null;
    }
  }, [composerText]);

  const createUuid = useCallback(() => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
  }, []);

  const updatePin = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pinId: string, updater: (pin: PinType) => any) => {
      setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin) : pin)));
    },
    [setPins]
  );

  const deletePin = useCallback(
    (pinId: string) => {
      setPins(prev => prev.filter(p => p.id !== pinId));
      if (activePinId === pinId) {
        setActivePinId(null);
      }
    },
    [activePinId, setActivePinId, setPins]
  );

  const handleDeletePin = useCallback(
    (pinId: string) => {
      const sessionId = currentSessionId;

      // Fallback to local-only delete if no session
      if (!sessionId) {
        deletePin(pinId);
        return;
      }

      // Check if pin exists in backend:
      // - Has any messages (user messages from sending, or AI messages from server hydration)
      // - Or has a non-draft state (was processed by server)
      const pin = pins.find(p => p.id === pinId);
      const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

      // If pin hasn't been synced to backend yet, just delete locally
      if (!hasBeenSyncedToBackend) {
        if (currentArtworkId && currentSessionId) {
          trackDesignLabPinDeleted({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId,
            pinState: pin?.state ?? "draft",
            hadMessages: false
          });
        }
        deletePin(pinId);
        return;
      }

      // Prevent duplicate delete requests for the same pin
      if (removingPinIds.has(pinId)) {
        return;
      }

      setRemovingPinIds(prev => new Set(prev).add(pinId));

      removePinApi(
        { sessionId, pinId },
        {
          onSuccess: () => {
            // Track successful pin deletion
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleted({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId,
                pinState: pin?.state ?? "draft",
                hadMessages: (pin?.messages?.length ?? 0) > 0
              });
            }
            deletePin(pinId);
            clearRemovingPin(pinId);
          },
          onError: () => {
            // Track failed pin deletion
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleteFailed({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId
              });
            }
            clearRemovingPin(pinId);
            showErrorToast({
              title: "Failed to remove pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [
      currentArtworkId,
      currentSessionId,
      removePinApi,
      deletePin,
      pins,
      removingPinIds,
      clearRemovingPin,
      showErrorToast
    ]
  );

  const openDeletePinModal = useCallback((pinId: string) => {
    setPendingDeletePinId(pinId);
  }, []);

  const closeDeletePinModal = useCallback(() => {
    setPendingDeletePinId(null);
  }, []);

  const confirmDeletePin = useCallback(() => {
    if (!pendingDeletePinId) return;
    handleDeletePin(pendingDeletePinId);
    setPendingDeletePinId(null);
  }, [handleDeletePin, pendingDeletePinId]);

  const { mutate: submitMessage } = useSubmitPinMessage({
    onAcknowledgment: (payload: AcknowledgmentPayload) => {
      if (!activePinId) return;
      updatePin(activePinId, pin => ({
        ...pin,
        thinkingText: payload.message || undefined
      }));
    },
    onResult: (payload: ResultPayload) => {
      if (!activePinId) return;

      const nextState =
        payload.status === "ready" ||
        payload.status === "needs_clarification" ||
        payload.status === "unsupported"
          ? payload.status
          : "ready";

      const clarifications = payload.clarifications ?? [];
      const resultMessage = payload.message || "Done.";

      // Get current pin to calculate message counts (AI message will be added)
      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      // Filter to clarifications with status "asking" and at least one option
      const askingClarifications = clarifications.filter(
        c => c.status === "asking" && c.options.length > 0
      );
      const hasClarifications =
        nextState === "needs_clarification" && askingClarifications.length > 0;

      updatePin(activePinId, pin => {
        const areasOfInterestUpdate = payload.aoiResult
          ? {
              areasOfInterest: payload.aoiResult,
              highlightAreasEnabled: pin.highlightAreasEnabled ?? true
            }
          : {};

        return {
          ...pin,
          state: nextState,
          instruction: payload.instruction,
          thinkingText: undefined, // Clear thinking state
          ...areasOfInterestUpdate,
          messages: [
            ...pin.messages,
            {
              id: createUuid(),
              role: "ai",
              content: resultMessage,
              timestamp: Date.now(),
              ...(hasClarifications && { clarifications, textInfo: payload.text })
            }
          ]
        };
      });

      // Reset clarification responses when new clarifications arrive
      if (hasClarifications) {
        setClarificationResponses({});
      }

      // Track pin state changes
      if (currentArtworkId && currentSessionId) {
        const trackingData = {
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          userMessagesCount,
          aiMessagesCount
        };

        if (nextState === "ready") {
          trackDesignLabPinReady(trackingData);
        } else if (nextState === "unsupported") {
          trackDesignLabPinUnsupported(trackingData);
        } else if (nextState === "needs_clarification") {
          trackDesignLabPinNeedsClarification({
            ...trackingData,
            optionsCount: askingClarifications.reduce((sum, c) => sum + c.options.length, 0),
            questionType: askingClarifications.map(c => c.type).join(",")
          });
        }
      }
    },
    onError: () => {
      if (!activePinId) return;

      // Get current pin to calculate message counts (AI error message will be added)
      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      // Track pin error
      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinError({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          errorType: "processing_error",
          userMessagesCount,
          aiMessagesCount
        });
      }

      updatePin(activePinId, pin => ({
        ...pin,
        state: "draft",
        messages: [
          ...pin.messages,
          {
            id: createUuid(),
            role: "ai",
            content: "Something went wrong. Please try again.",
            timestamp: Date.now()
          }
        ]
      }));
    }
  });

  const isComposerTextValid = useCallback(
    (text: string) => text.trim().length >= MIN_MESSAGE_LENGTH,
    []
  );

  const lastAiMessageWithClarifications = useMemo(() => {
    if (!activePin) return null;
    for (let i = activePin.messages.length - 1; i >= 0; i -= 1) {
      const msg = activePin.messages[i];
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  }, [activePin]);

  const pendingPillClarifications = useMemo(() => {
    const clarifications = lastAiMessageWithClarifications?.clarifications ?? [];
    return clarifications.filter(c => c.status === "asking" && c.options.length > 0);
  }, [lastAiMessageWithClarifications]);

  // Find font and color clarifications for cross-updating
  const fontClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_selection"),
    [pendingPillClarifications]
  );
  const colorClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_color"),
    [pendingPillClarifications]
  );

  // Get selected font and color values for cross-updating previews
  const selectedFontValue = useMemo(() => {
    if (!fontClarification) return null;
    const response = clarificationResponses[fontClarification.question];
    if (response?.isTypingCustom) return response.custom || null;
    return response?.selected || null;
  }, [fontClarification, clarificationResponses]);

  const selectedColorValue = useMemo(() => {
    if (!colorClarification) return null;
    const response = clarificationResponses[colorClarification.question];
    if (response?.isTypingCustom) return response.custom || null;
    return response?.selected || null;
  }, [colorClarification, clarificationResponses]);

  // Helper function to calculate unanswered questions above/below viewport
  const calculateUnansweredPositions = useCallback(() => {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications.forEach(clarification => {
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          // Check if element is above the visible area
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          }
          // Check if element is below the visible area
          else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    setUnansweredAbove(above);
    setUnansweredBelow(below);
  }, [pendingPillClarifications, clarificationResponses]);

  // Helper to scroll to first unanswered clarification in a given direction
  const scrollToFirstUnanswered = useCallback(
    (direction: "above" | "below") => {
      const scrollContainer = clarificationScrollRef.current;
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();

      for (const clarification of pendingPillClarifications) {
        const isUnanswered = !getClarificationAnswer(
          clarificationResponses[clarification.question]
        );

        if (isUnanswered) {
          const labelValue = clarification.label || clarification.question;
          const element = scrollContainer.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );

          if (element) {
            const elementRect = element.getBoundingClientRect();
            const isTargetDirection =
              direction === "above"
                ? elementRect.bottom < containerRect.top
                : elementRect.top > containerRect.bottom;

            if (isTargetDirection) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
              break;
            }
          }
        }
      }
    },
    [pendingPillClarifications, clarificationResponses]
  );

  // Check unanswered positions when clarifications are loaded or responses change
  useEffect(() => {
    if (pendingPillClarifications.length > 0) {
      calculateUnansweredPositions();
    }
  }, [pendingPillClarifications.length, clarificationResponses, calculateUnansweredPositions]);

  const handleSelectClarificationOption = useCallback(
    (question: string, value: string | null) => {
      setClarificationResponses(prev => {
        // Check if this question was previously unanswered
        const wasUnanswered = !getClarificationAnswer(prev[question]);

        const newResponses = {
          ...prev,
          [question]: {
            selected: value,
            custom: prev[question]?.custom ?? "",
            isTypingCustom: false
          }
        };

        if (currentArtworkId && currentSessionId && activePinId && activePin) {
          trackDesignLabPinClarificationClicked({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId: activePinId,
            optionValue: value || "",
            turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
            userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
            aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
            optionType: "suggested"
          });
        }

        // Only auto-scroll if this is a first-time answer (not changing an existing answer)
        if (value !== null && wasUnanswered) {
          setTimeout(() => {
            const currentIndex = pendingPillClarifications.findIndex(c => c.question === question);

            // Rotational search: start from next question, wrap around if needed
            const reorderedClarifications = [
              ...pendingPillClarifications.slice(currentIndex + 1),
              ...pendingPillClarifications.slice(0, currentIndex)
            ];
            const nextUnanswered = reorderedClarifications.find(
              c => !getClarificationAnswer(newResponses[c.question])
            );

            if (nextUnanswered) {
              const scrollContainer = clarificationScrollRef.current;
              const labelValue = nextUnanswered.label || nextUnanswered.question;
              const targetElement = scrollContainer?.querySelector(
                `[data-clarification-label="${CSS.escape(labelValue)}"]`
              );
              if (targetElement && scrollContainer) {
                targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }, 100);
        }

        return newResponses;
      });
    },
    [pendingPillClarifications, currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleToggleCustomInput = useCallback(
    (question: string) => {
      setClarificationResponses(prev => ({
        ...prev,
        [question]: {
          selected: prev[question]?.selected ?? null,
          custom: prev[question]?.custom ?? "",
          isTypingCustom: !prev[question]?.isTypingCustom
        }
      }));

      if (currentArtworkId && currentSessionId && activePinId && activePin) {
        trackDesignLabPinClarificationClicked({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          optionValue: "",
          turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
          userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
          aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
          optionType: "custom"
        });
      }
    },
    [currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleCustomInputChange = useCallback((question: string, value: string) => {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        selected: prev[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    }));
  }, []);

  const formatClarificationResponses = useCallback(() => {
    const parts: string[] = [];
    const analyticsClarifications: AnalyticsClarification[] = [];

    for (const clarification of pendingPillClarifications) {
      const value = getClarificationAnswer(clarificationResponses[clarification.question]);
      const optionType = clarificationResponses[clarification.question]?.isTypingCustom
        ? "custom"
        : "suggested";
      analyticsClarifications.push({
        question: clarification.question,
        value: value || "",
        option_type: optionType
      });
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
      }
    }

    return { clarificationText: parts.join("\n"), clarifications: analyticsClarifications };
  }, [pendingPillClarifications, clarificationResponses]);

  const hasAnyClarificationResponse = useMemo(
    () =>
      pendingPillClarifications.some(c =>
        getClarificationAnswer(clarificationResponses[c.question])
      ),
    [pendingPillClarifications, clarificationResponses]
  );

  const handleSubmit = useCallback(() => {
    if (!activePin) return;
    if (activePin.state === "processing") return;
    const sessionId = currentSessionId ?? null;

    // Build the full message from clarification responses + composer text
    const { clarificationText, clarifications } = formatClarificationResponses();
    const messageText = composerText.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    const pinId = activePin.id;
    const now = Date.now();
    const userMessageId = createUuid();
    const isFirstMessage = activePin.messages.filter(m => m.role === "user").length === 0;
    const turnIndex = activePin.messages.filter(m => m.role === "user").length + 1;

    // Optimistic user message + processing state
    updatePin(pinId, pin => ({
      ...pin,
      state: "processing" as const,
      thinkingText: undefined, // Will show default "Thinking..."
      messages: [
        ...pin.messages.map(m => ({ ...m, clarifications: undefined })), // clear prior clarifications
        { id: userMessageId, role: "user" as const, content: fullMessage, timestamp: now }
      ]
    }));
    setComposerText("");
    setClarificationResponses({});

    // Track message submission or clarification (user message being added)
    const { userMessagesCount, aiMessagesCount } = getMessageCounts(activePin.messages, "user");

    if (currentArtworkId && currentSessionId) {
      trackDesignLabPinMessageSubmitted({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    if (!sessionId) {
      return;
    }

    const priorPins = pins
      .filter(p => p.id !== pinId)
      .map(p => ({
        pin_id: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    submitMessage({
      sessionId,
      pinId,
      coordinates: activePin.coordinates,
      userMessage: fullMessage,
      isFirstMessage,
      priorPins
    });
  }, [
    activePin,
    composerText,
    createUuid,
    currentArtworkId,
    currentSessionId,
    formatClarificationResponses,
    pins,
    submitMessage,
    updatePin
  ]);

  return (
    <Card className="flex h-full w-full max-w-[700px] flex-col border-none shadow-none lg:border lg:shadow">
      <ConfirmActionModal
        isOpen={pendingDeletePinId !== null}
        onClose={closeDeletePinModal}
        onConfirm={confirmDeletePin}
        title="Delete pin?"
        message="Are you sure you want to delete the pin?"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <CardContent className="flex flex-1 flex-col overflow-hidden p-0">
        <div className="flex flex-1 flex-col overflow-y-auto p-4">
          {pins.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-gray-500">
              <Pin className="mb-4 h-16 w-16 -rotate-45 text-gray-300" />
              <p className="max-w-[280px] text-sm leading-relaxed">
                No instructions added yet. Please click anywhere on the image to drop a pin and add
                instructions for our designers.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 pb-4">
              {pins.map((pin, index) => {
                const isActive = pin.id === activePinId;
                const isProcessing = pin.state === "processing";
                const isCompleted = pin.state === "ready" || pin.state === "unsupported";
                const shouldConfirmDelete = isCompleted;

                return (
                  <div
                    key={pin.id}
                    className={`flex flex-col gap-3 rounded-lg border p-4 transition-colors ${
                      isActive ? "border-blue-200 bg-blue-50/30" : "border-gray-200 bg-white"
                    }`}
                    onMouseEnter={() => {
                      if (isCompleted) {
                        setHoveredPinId(pin.id);
                        setHoveredPinSource("panel");
                      }
                    }}
                    onMouseLeave={() => {
                      if (isCompleted) {
                        setHoveredPinId(null);
                        setHoveredPinSource(null);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="flex cursor-pointer items-center gap-2 font-medium"
                        onClick={() => {
                          // Prevent switching/deselecting when active pin is not ready
                          const isActivePinNotReady =
                            activePin &&
                            activePin.state !== "ready" &&
                            activePin.state !== "unsupported";
                          if (isActivePinNotReady) {
                            return;
                          }

                          const previousPinId = activePinId;
                          const newPinId = isActive ? null : pin.id;
                          setActivePinId(newPinId);

                          // Track pin selection (only when selecting a pin, not deselecting)
                          if (newPinId && currentArtworkId && currentSessionId) {
                            trackDesignLabPinSelected({
                              artworkId: currentArtworkId,
                              sessionId: currentSessionId,
                              pinId: newPinId,
                              previousPinId,
                              pinState: pin.state
                            });
                          }
                        }}
                      >
                        <span className="text-sm font-semibold text-gray-900">
                          Pin {index + 1}:{" "}
                          <span className="font-normal text-gray-500">
                            What would you like to do?
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`pin-delete-${pin.id}`}
                          aria-label={`Delete pin ${pin.id}`}
                          onPress={() =>
                            shouldConfirmDelete
                              ? openDeletePinModal(pin.id)
                              : handleDeletePin(pin.id)
                          }
                          variant="icon"
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                          isDisabled={removingPinIds.has(pin.id)}
                        >
                          {removingPinIds.has(pin.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Content area: either messages list or final instruction */}
                    {(pin.messages.length > 0 || (pin.state === "ready" && pin.instruction)) && (
                      <div className="flex flex-col gap-4">
                        {pin.state === "ready" && pin.instruction ? (
                          <div className="rounded-lg border border-[#A5B4FC] bg-white p-4 shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)]">
                            <div className="flex items-start gap-3">
                              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#6366F1]" />
                              <div className="flex flex-col gap-1">
                                <h4 className="text-xs font-bold uppercase tracking-wide text-[#6366F1]">
                                  WE’VE GOT YOUR BACK — OUR DESIGNERS WILL UPDATE THE DESIGN BASED
                                  ON YOUR INSTRUCTIONS BELOW
                                </h4>
                                <p className="text-sm text-gray-700">
                                  {processTextWithHexCodes(pin.instruction || "")}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-4 pl-1">
                            {pin.messages.map(msg => (
                              <div key={msg.id} className="flex flex-col gap-1 text-sm">
                                <span
                                  className={`text-[10px] font-bold uppercase ${msg.role === "user" ? "text-gray-400" : "text-blue-700"}`}
                                >
                                  {msg.role === "user" ? "ME" : "REDRAW ASSISTANT"}
                                </span>
                                {msg.role === "ai" ? (
                                  <MarkdownContent content={msg.content} />
                                ) : (
                                  <div className="whitespace-pre-wrap text-gray-800">
                                    {processTextWithHexCodes(msg.content)}
                                  </div>
                                )}
                              </div>
                            ))}
                            {pin.state === "processing" && (
                              <div className="flex flex-col gap-1 text-sm">
                                <span className="text-[10px] font-bold uppercase text-blue-700">
                                  REDRAW ASSISTANT
                                </span>
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  <span className="text-sm text-gray-500">
                                    {pin.thinkingText || "Thinking..."}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {isActive && pin.state !== "ready" && (
                      <div className={pin.messages.length > 0 ? "mt-2" : ""}>
                        {pin.state === "needs_clarification" &&
                        pendingPillClarifications.length > 0 ? (
                          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                            {/* Question counter */}
                            <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
                              <span>
                                {pendingPillClarifications.length} question
                                {pendingPillClarifications.length > 1 ? "s" : ""} to answer
                              </span>
                              <span>
                                {
                                  pendingPillClarifications.filter(c =>
                                    getClarificationAnswer(clarificationResponses[c.question])
                                  ).length
                                }{" "}
                                of {pendingPillClarifications.length} answered
                              </span>
                            </div>
                            {/* Scrollable container with position indicators */}
                            <div className="relative">
                              <div
                                ref={clarificationScrollRef}
                                className="flex flex-col gap-4 overflow-y-auto pr-1"
                                style={{ maxHeight: CLARIFICATION_CONTAINER_MAX_HEIGHT }}
                                onScroll={calculateUnansweredPositions}
                              >
                                {pendingPillClarifications.map(clarification => (
                                  <ClarificationSection
                                    key={clarification.question}
                                    clarification={clarification}
                                    response={
                                      clarificationResponses[clarification.question] ?? {
                                        selected: null,
                                        custom: "",
                                        isTypingCustom: false
                                      }
                                    }
                                    onSelectOption={value =>
                                      handleSelectClarificationOption(clarification.question, value)
                                    }
                                    onToggleCustomInput={() =>
                                      handleToggleCustomInput(clarification.question)
                                    }
                                    onCustomInputChange={value =>
                                      handleCustomInputChange(clarification.question, value)
                                    }
                                    onSubmit={handleSubmit}
                                    isDisabled={isProcessing}
                                    textInfo={lastAiMessageWithClarifications?.textInfo}
                                    selectedFont={selectedFontValue}
                                    selectedColor={selectedColorValue}
                                  />
                                ))}
                              </div>
                              {/* Unanswered above indicator - absolute positioned */}
                              {unansweredAbove > 0 && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  data-testid="unanswered-above"
                                  aria-label={`Scroll to ${unansweredAbove} unanswered question${unansweredAbove > 1 ? "s" : ""} above`}
                                  onClick={() => scrollToFirstUnanswered("above")}
                                  onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      scrollToFirstUnanswered("above");
                                    }
                                  }}
                                  className="absolute -top-1 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white shadow-md transition-opacity hover:bg-blue-600"
                                >
                                  ↑ {unansweredAbove} unanswered above
                                </div>
                              )}
                              {/* Unanswered below indicator - absolute positioned */}
                              {unansweredBelow > 0 && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  data-testid="unanswered-below"
                                  aria-label={`Scroll to ${unansweredBelow} unanswered question${unansweredBelow > 1 ? "s" : ""} below`}
                                  onClick={() => scrollToFirstUnanswered("below")}
                                  onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      scrollToFirstUnanswered("below");
                                    }
                                  }}
                                  className="absolute -bottom-1 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white shadow-md transition-opacity hover:bg-blue-600"
                                >
                                  {unansweredBelow} unanswered below ↓
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}

                        <div className="flex gap-2">
                          <textarea
                            ref={textareaRef}
                            value={composerText}
                            onChange={e => setComposerText(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                if (e.metaKey || e.ctrlKey) {
                                  // Cmd+Enter (macOS) or Ctrl+Enter (Windows/Linux) inserts a newline
                                  e.preventDefault();
                                  const { selectionStart, selectionEnd } = e.currentTarget;
                                  const newValue = `${composerText.slice(
                                    0,
                                    selectionStart
                                  )}\n${composerText.slice(selectionEnd)}`;
                                  pendingCursorPositionRef.current = selectionStart + 1;
                                  setComposerText(newValue);
                                  return;
                                }
                                // Enter alone submits the message
                                e.preventDefault();
                                const canSubmit =
                                  hasAnyClarificationResponse || isComposerTextValid(composerText);
                                if (!isProcessing && canSubmit) {
                                  handleSubmit();
                                }
                              }
                            }}
                            placeholder="Add your instructions here..."
                            className="min-h-[44px] flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                            disabled={isProcessing}
                            data-testid="pinning-composer"
                          />
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <Button
                            data-testid="pin-composer-cancel"
                            variant="link"
                            className="text-xs font-medium text-gray-500 no-underline hover:text-gray-700 hover:no-underline"
                            onPress={() =>
                              shouldConfirmDelete
                                ? openDeletePinModal(pin.id)
                                : handleDeletePin(pin.id)
                            }
                            isDisabled={removingPinIds.has(pin.id)}
                          >
                            {removingPinIds.has(pin.id) ? "Removing..." : "Cancel"}
                          </Button>
                          <Button
                            data-testid="pinning-send"
                            className="rounded bg-blue-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                            isDisabled={
                              isProcessing ||
                              (!hasAnyClarificationResponse && !isComposerTextValid(composerText))
                            }
                            onPress={handleSubmit}
                          >
                            Submit
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
